import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Layout Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import FreebiePage from "./pages/FreebiePage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import ContactPage from "./pages/ContactPage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import { CheckoutSuccess, CheckoutCancel, PayPalSuccess } from "./pages/CheckoutPages";
import { GuideDetail, PackDetail, GuideSuccess } from "./pages/GuidePages";
import MyOrdersPage from "./pages/MyOrdersPage";
import RegionPage from "./pages/RegionPage";
import CountryPage from "./pages/CountryPage";
import { PracticeBanner } from "./lib/practice";
import ProductPage from "./pages/ProductPage";
import DestinationPage from "./pages/DestinationPage";
import UpgradePage from "./pages/UpgradePage";
import { RouteTracker } from "./lib/analytics";

const OldRegionRedirect = () => <Navigate to={`/shop/regions/${useParams().slug}`} replace />;

// Layout wrapper
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">{children}</main>
      {showFooter && <Footer />}
      <PracticeBanner />
      <Toaster position="bottom-right" />
    </div>
  );
};

// Admin layout without footer
const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-grow">{children}</main>
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout><HomePage /></Layout>} />
          <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
          <Route path="/shop/guides/:slug" element={<Layout><GuideDetail /></Layout>} />
          <Route path="/shop/packs/:slug" element={<Layout><PackDetail /></Layout>} />
          <Route path="/guides/success" element={<Layout><GuideSuccess /></Layout>} />
          <Route path="/orders" element={<Layout><MyOrdersPage /></Layout>} />
          <Route path="/shop/regions/:slug" element={<Layout><RegionPage /></Layout>} />
          <Route path="/shop/countries/:slug" element={<Layout><CountryPage /></Layout>} />
          <Route path="/regions/:slug" element={<OldRegionRedirect />} />
          <Route path="/shop/p/:id" element={<Layout><ProductPage /></Layout>} />
          <Route path="/destinations/:slug" element={<Layout><DestinationPage /></Layout>} />
          <Route path="/upgrade" element={<Layout><UpgradePage /></Layout>} />
          <Route path="/freebie" element={<Layout><FreebiePage /></Layout>} />
          <Route path="/about" element={<Layout><AboutPage /></Layout>} />
          <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
          <Route path="/blog/:postId" element={<Layout><BlogPage /></Layout>} />
          <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
          
          {/* Legal Pages */}
          <Route path="/privacy" element={<Layout><PlaceholderPage title="Privacy Policy" /></Layout>} />
          <Route path="/terms" element={<Layout><PlaceholderPage title="Terms of Service" /></Layout>} />
          <Route path="/refunds" element={<Layout><PlaceholderPage title="Refund Policy" /></Layout>} />
          
          {/* Checkout Pages */}
          <Route path="/checkout/success" element={<Layout><CheckoutSuccess /></Layout>} />
          <Route path="/checkout/cancel" element={<Layout><CheckoutCancel /></Layout>} />
          <Route path="/checkout/paypal-success" element={<Layout><PayPalSuccess /></Layout>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Layout showFooter={false}><AdminLogin /></Layout>} />
          <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Simple placeholder for legal pages
const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen pt-24 pb-20">
    <div className="container-custom">
      <h1 className="font-serif text-4xl font-semibold text-[#1E1622] mb-6">{title}</h1>
      <p className="text-[#1E1622]/70">This page is coming soon.</p>
    </div>
  </div>
);

export default App;
